import { isDefined } from "@clipboard-health/util-ts";
import { ButtonBase, Collapse, Stack } from "@mui/material";
import { Card, CbhIcon, type IconType, Text } from "@src/appV2/redesign/system/ui-components";
import type { ReactElement } from "react";
import { cloneElement, useCallback, useState } from "react";

import { DescriptionRenderer } from "./AccordionDescription";

export interface MultiLineDescription {
  primary: string;
  secondary?: string;
}

export type AccordionDescription = string | MultiLineDescription;

export interface AccordionItemProps {
  onDescriptionChange?: (description: AccordionDescription) => void;
}

export interface AccordionCardProps {
  label: string;
  subtitle?: string;
  icon?: IconType;
  children?: ReactElement<AccordionItemProps>;
  isExpanded?: boolean;
  onToggle?: () => void;
  initialDescription?: AccordionDescription;
}

export function AccordionCard(props: AccordionCardProps) {
  const {
    label,
    subtitle,
    icon,
    children,
    isExpanded: controlledExpanded,
    onToggle,
    initialDescription,
  } = props;
  const [internalExpanded, setInternalExpanded] = useState(false);

  const isExpanded = controlledExpanded ?? internalExpanded;

  const [description, setDescription] = useState<AccordionDescription | undefined>(
    initialDescription
  );

  const handleDescriptionChange = useCallback((newDescription: AccordionDescription) => {
    setDescription(newDescription);
  }, []);

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  return (
    <Card outlined elevation={2} variant="tertiary">
      {!isExpanded && (
        <ButtonBase sx={{ width: "100%", px: 4, py: 7 }} onClick={handleToggle}>
          <Stack direction="row" alignItems="center" spacing={4} sx={{ width: "100%" }}>
            {/* Icon column */}
            {icon && (
              <CbhIcon
                type={icon}
                size="medium"
                sx={(theme) => ({ color: theme.palette.text.primary })}
              />
            )}

            {/* Label and subtitle column */}
            <Stack flex={1} alignItems="flex-start">
              <Text semibold sx={(theme) => ({ color: theme.palette.text.secondary })}>
                {label}
              </Text>
              {subtitle && (
                <Text bold variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
                  {subtitle}
                </Text>
              )}
            </Stack>

            {/* Description column */}
            {isDefined(description) && <DescriptionRenderer description={description} />}

            {/* Chevron indicator */}
            <CbhIcon
              type="chevron-right"
              size="small"
              sx={(theme) => ({ color: theme.palette.text.tertiary })}
            />
          </Stack>
        </ButtonBase>
      )}

      <Collapse in={isExpanded}>
        <Stack>
          {children && cloneElement(children, { onDescriptionChange: handleDescriptionChange })}
        </Stack>
      </Collapse>
    </Card>
  );
}
